import { Filter } from 'types';
import {
  NewCentralFormType,
  SpecificFilterOption,
  SpecificFilterRow,
} from '../types';
import {
  InitialBody,
  InitialResultDto,
  ParamsMap,
  SearchEntityEnum,
  SearchEntityType,
  searchEntityTypeList,
} from './search-v3-types';

export const getEmptyParamsMap = (): ParamsMap => ({
  ARCHIVAL_AID: [],
  ARCHIVAL_COLLECTION: [],
  EAD_NODE_UNIT: [],
  ORIGINATOR: [],
});

export const createParamsMap = (
  rowFilters: Filter[],
  rows: SpecificFilterRow[]
): InitialBody => {
  const paramsMap = getEmptyParamsMap();
  const cascadingParamsMap = getEmptyParamsMap();

  Object.keys(paramsMap).forEach((key) => {
    const filters = rowFilters
      .filter((_, index) => rows[index].filterOption.searchIn?.includes(key))
      .map((f) => f);

    const cascading = rowFilters
      .filter(
        (_, index) =>
          rows[index].filterOption.cascading &&
          rows[index].filterOption.searchIn?.includes(key)
      )
      .map((f) => f);
    paramsMap[key as SearchEntityEnum] = filters;
    cascadingParamsMap[key as SearchEntityEnum] = cascading;
  });

  return { paramsMap, cascadingParamsMap };
};

export const mergeParamsMap = (
  paramsMaps: (ParamsMap | undefined)[]
): ParamsMap => {
  const merged = getEmptyParamsMap();

  Object.keys(merged).forEach((key) => {
    const pkey = key as SearchEntityEnum;
    paramsMaps
      .filter((pm) => pm !== undefined)
      .forEach((pm) => {
        merged[key as SearchEntityEnum]?.push(...(pm?.[pkey] ?? []));
      });
  });

  return merged;
};

export const getTextFilter = (
  field: string,
  words: string[],
  textOperation: string
): Filter => {
  if (textOperation === 'EQ' || textOperation === 'MATCH_PHRASE') {
    return {
      field,
      operation: textOperation,
      value: words[0],
      nestedQueryEnabled: false,
    };
  }
  if (textOperation === 'CONTAINS') {
    return {
      operation: 'OR',
      filters: words.map((w) => ({
        field,
        operation: 'CONTAINS',
        value: w,
        nestedQueryEnabled: false,
      })),
    };
  }
  //if (textOperation === 'CONTAINS_AND_CONTAINS') {
  return {
    operation: 'AND',
    filters: words.map((w) => ({ field, operation: 'CONTAINS', value: w })),
    nestedQueryEnabled: false,
  };
};

export const getTimeRangeFilter = (
  filterValue: SpecificFilterRow['timeRangeFilterValue'],
  fieldNames: { from: string; to: string }
): Filter => {
  const filters: Filter[] = [];
  if (filterValue?.from) {
    filters.push({
      operation: 'GTE',
      field: fieldNames.to,
      value: `${filterValue?.from}T00:00:00`,
    });
  }

  if (filterValue?.to) {
    filters.push({
      operation: 'LTE',
      field: fieldNames.from,
      value: `${filterValue?.to}T23:59:59`,
    });
  }
  return { operation: 'AND', filters };
};

const getWordSegmenter = () => {
  return new Intl.Segmenter(undefined, { granularity: 'word' });
};

export const getWordsAndOperation = (
  textOperation = 'CONTAINS',
  value?: string
) => {
  if (!value) {
    return { textOperation, words: [] };
  }
  if (textOperation === 'EQ' || textOperation === 'MATCH_PHRASE') {
    return { textOperation, words: [value] };
  }

  const segmenter = getWordSegmenter();
  const segments = segmenter.segment(value);
  const words = Array.from(segments)
    .filter((segment) => segment.isWordLike)
    .map((segment) => segment.segment);

  return { textOperation, words };
};

export const getInitialResultActiveTab = (
  result: InitialResultDto,
  selectedEntityType: SearchEntityEnum
): SearchEntityEnum | undefined => {
  if ((result.resultMap?.[selectedEntityType]?.count ?? 0) !== 0) {
    return selectedEntityType;
  }

  return Object.keys(result.resultMap).find(
    (key) => (result.resultMap?.[key as SearchEntityEnum]?.count ?? 0) !== 0
  ) as SearchEntityEnum | undefined;
};

export const getNegationFilter = (
  filterBody: Filter,
  negation: boolean
): Filter =>
  negation ? { operation: 'NOT', filters: [filterBody] } : filterBody;

export const isInitialResultEmpty = (intialResult: InitialResultDto) =>
  !intialResult ||
  !intialResult.resultMap ||
  Object.keys(intialResult.resultMap).length === 0 ||
  Object.keys(intialResult.resultMap).every(
    (k) => intialResult.resultMap[k as SearchEntityType].count === 0
  );

export const getSearchInListForOption = (option: SpecificFilterOption) => {
  const result = [...(option.searchIn ?? [])];
  searchEntityTypeList.forEach((type) => {
    if ((option?.targetFieldsComplex_v3?.[type]?.length ?? 0) > 0) {
      result.push(type);
    }
  });

  return result;
};

export const getAllValidSearchSpaces = (values: NewCentralFormType) => {
  // const searchSpaces = values.specificFilterRows
  //   .map((r) => r.filterOption.category.validSearchSpace)
  //   .flat();
  // return _.uniq(searchSpaces);

  return getSearchSpaceIntersection(
    (values.specificFilterRows ?? []).map((r) => r.filterOption)
  );
};

const intersect = <T>(array1: T[], array2: T[]) =>
  array1.filter((value) => array2.includes(value));

export const getSearchSpaceIntersection_USING_VALUES_TODELETE = (
  values: NewCentralFormType
) => {
  let ALL = [
    SearchEntityEnum.ARCHIVAL_AID,
    SearchEntityEnum.ARCHIVAL_COLLECTION,
    SearchEntityEnum.EAD_NODE_UNIT,
    SearchEntityEnum.ORIGINATOR,
  ];
  values.specificFilterRows.forEach((r) => {
    ALL = intersect(r.filterOption.category.validSearchSpace, ALL);
  });

  return ALL;
};

export const getSearchSpaceIntersection = (
  options?: (SpecificFilterOption | undefined)[]
) => {
  let ALL = [
    SearchEntityEnum.ARCHIVAL_AID,
    SearchEntityEnum.ARCHIVAL_COLLECTION,
    SearchEntityEnum.EAD_NODE_UNIT,
    SearchEntityEnum.ORIGINATOR,
  ];

  (options ?? []).forEach((option) => {
    const validSearchSpace = option?.category?.validSearchSpace;
    if (!validSearchSpace) {
      return;
    }

    ALL = intersect(validSearchSpace, ALL);
  });

  return ALL;
};

export const keepValidSpacesOnly = (
  values: NewCentralFormType,
  paramsMap: ParamsMap
) => {
  const validSearchSpace = getAllValidSearchSpaces(values);
  console.log('validSearchSpace = ', validSearchSpace);
  const result = {} as ParamsMap;
  Object.keys(paramsMap).forEach((key) => {
    const pkey = key as SearchEntityEnum;
    if (validSearchSpace.includes(pkey)) {
      result[pkey] = paramsMap[pkey];
    } else {
      if (validSearchSpace.includes(pkey)) {
        result[pkey] = paramsMap[pkey];
      }
    }
  });
  return result;
};
